import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import BetaAccess from "@/pages/BetaAccess";
import ReviewDashboard from "@/pages/ReviewDashboard";
import SetupWizard from "@/pages/SetupWizard";
import Budget from "@/pages/Budget";
import Settings from "@/pages/Settings";
import CertPrep from "@/pages/CertPrep";
import Dashboard from "@/pages/Dashboard";
import Tasks from "@/pages/Tasks";
import Incidents from "@/pages/Incidents";
import FlashMeeting from "@/pages/FlashMeeting";
import Rounds from "@/pages/Rounds";
import Shutdown from "@/pages/Shutdown";
import Weekly from "@/pages/Weekly";
import AIAssistant from "@/pages/AIAssistant";
import Sprints from "@/pages/Sprints";
import CommunicationStudio from "@/pages/CommunicationStudio";
import TrainingCenter from "@/pages/TrainingCenter";
import LeadershipAcademy from "@/pages/LeadershipAcademy";
import CalendarPage from "@/pages/Calendar";

// Components
import { Sidebar, MobileHeader } from "@/components/Sidebar";
import { QuickCapture } from "@/components/QuickCapture";
import VoiceAssistant from "@/components/VoiceAssistant";
import { BrainDumpProvider } from "@/components/BrainDumpProvider";
import BottomTabBar from "@/components/BottomTabBar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Safari / ITP–safe auth: attach Authorization: Bearer header from
// localStorage on every API request. Cookies remain the primary mechanism
// (set by the backend with HttpOnly + SameSite=None), but Safari aggressively
// drops cross-site cookies after an OAuth redirect through a different
// subdomain. Sending a Bearer header in parallel makes the auth flow work
// reliably in every browser without compromising security (the token is the
// same one stored in the cookie, scoped to this single user).
// ---------------------------------------------------------------------------
export const SESSION_TOKEN_KEY = "niko_session_token";

axios.interceptors.request.use((config) => {
  const url = config.url || "";
  if (url.includes("/api/")) {
    const token = localStorage.getItem(SESSION_TOKEN_KEY);
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    // On 401, clear the token so the next render bounces to /login cleanly
    if (err?.response?.status === 401) {
      localStorage.removeItem(SESSION_TOKEN_KEY);
    }
    return Promise.reject(err);
  }
);

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      // CRITICAL: If returning from OAuth callback, skip the /me check.
      // AuthCallback (rendered earlier in the tree when #session_id is in
      // the URL) handles the session exchange. Hitting /api/auth/me here
      // would 401 before the cookie is set and bounce the user back to
      // /login — exactly the symptom users have reported.
      if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
        return; // leave isAuthenticated as null; AuthCallback will navigate us
      }
      // Skip if user data passed from AuthCallback
      if (location.state?.user) {
        setUser(location.state.user);
        setIsAuthenticated(true);
        return;
      }
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });
        if (response.data) {
          setUser(response.data);
          setIsAuthenticated(true);
          // Beta gate: if email is not on the allowlist, send them to the
          // request-access page (they can redeem a code there). Owners are
          // always allowed by definition. The /beta/access route itself is
          // wrapped in ProtectedRoute too — see exception below.
          if (response.data.allowed === false && location.pathname !== "/beta/access") {
            navigate("/beta/access", { replace: true });
            return;
          }
          // Setup gate: first-time users (no facilities, no setup_complete)
          // get sent to the wizard. Skip the check on the wizard itself, on
          // the beta gate, and on /review (owners may need to invite their
          // first testers before personalizing).
          if (response.data.allowed !== false &&
              !["/setup", "/beta/access", "/review"].includes(location.pathname)) {
            try {
              const setupRes = await axios.get(`${API}/setup/status`, { withCredentials: true });
              if (setupRes.data?.needs_wizard) {
                navigate("/setup", { replace: true });
                return;
              }
            } catch (_) { /* non-fatal — let user proceed */ }
          }
          // One-time: detect the browser's IANA timezone and sync it to the
          // backend preferences so nudge timestamps land correctly.
          try {
            const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const prefRes = await axios.get(`${API}/preferences`, { withCredentials: true });
            const remote = prefRes.data?.timezone;
            const isDefault = prefRes.data?.is_default;
            if (detected && (isDefault || (remote && remote !== detected && !localStorage.getItem("niko_tz_synced")))) {
              await axios.put(`${API}/preferences`, { timezone: detected }, { withCredentials: true });
              localStorage.setItem("niko_tz_synced", "1");
            }
          } catch (_) { /* non-fatal */ }
        }
      } catch (error) {
        console.log("Not authenticated");
        setIsAuthenticated(false);
        navigate("/login");
      }
    };

    checkAuth();
  }, [navigate, location.state, location.pathname, setUser, setIsAuthenticated]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      if (response.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, refreshUser }}>
      <BrainDumpProvider>{children}</BrainDumpProvider>
    </AuthContext.Provider>
  );
};

// Main Layout with Sidebar
const MainLayout = ({ children }) => {
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [facilities, setFacilities] = useState([]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  
  // Fetch facilities for voice assistant
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        const res = await axios.get(`${API}/facilities`, { withCredentials: true });
        setFacilities(res.data || []);
      } catch (error) {
        // Silent fail - voice assistant will work without facilities
      }
    };
    fetchFacilities();
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Mobile Header */}
      <MobileHeader onMenuClick={toggleSidebar} />
      
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
      
      {/* Main Content — overflow-x-hidden defense so any single rogue
          wide child can't trigger horizontal scroll on phones.
          pb-20 md:pb-0 reserves room for the phone-only BottomTabBar so
          content doesn't hide behind it. */}
      <main className="pt-14 lg:pt-0 lg:ml-64 p-4 md:p-6 lg:p-8 pb-24 md:pb-8 min-h-screen overflow-x-hidden">
        {children}
      </main>
      
      {/* Phone-only bottom tab bar — hidden from md+ where Sidebar takes over */}
      <BottomTabBar />
      
      {/* Quick Capture FAB - positioned above the bottom tab bar on phone,
          floating bottom-right on tablet+. */}
      <button
        data-testid="quick-capture-fab"
        onClick={() => setQuickCaptureOpen(true)}
        className="fixed bottom-20 md:bottom-6 right-24 z-40 w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg flex items-center justify-center transition-all no-print"
        aria-label="Quick Capture"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>

      <QuickCapture open={quickCaptureOpen} onClose={() => setQuickCaptureOpen(false)} />
      
      {/* Voice Assistant - floating button */}
      <VoiceAssistant 
        facilities={facilities}
        onTaskCreated={() => {/* Tasks will refresh on their own page */}}
        onMeetingCreated={() => {/* Calendar will refresh on its own page */}}
      />
    </div>
  );
};

// App Router Component
function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/beta/access" element={<BetaAccess />} />
      <Route path="/setup" element={<SetupWizard />} />
      <Route
        path="/review"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ReviewDashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Tasks />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CalendarPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/incidents"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Incidents />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/flash-meeting"
        element={
          <ProtectedRoute>
            <MainLayout>
              <FlashMeeting />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rounds"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Rounds />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shutdown"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Shutdown />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/weekly"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Weekly />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AIAssistant />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sprints"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Sprints />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/communication"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CommunicationStudio />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/training"
        element={
          <ProtectedRoute>
            <MainLayout>
              <TrainingCenter />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/leadership"
        element={
          <ProtectedRoute>
            <MainLayout>
              <LeadershipAcademy />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Budget />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Settings />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/certprep"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CertPrep />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        toastOptions={{
          duration: 5000,
          classNames: {
            toast: 'bg-zinc-900 border-zinc-800',
          }
        }}
      />
    </div>
  );
}

export default App;
